import type { Ref } from 'react';
import clsx from 'clsx';
import {
  Menu,
  MenuRadio,
  MenuRadioGroup,
  ProjectAvatar,
  Text,
  theme,
} from '@frameio/vapor';
import { IconChevronDown, IconProject } from '@frameio/vapor-icons';
import { useScopedToProject } from '../hooks/useScopedToProject';
import TEXT from './generated/TEXT/ProjectScopeDropdown';
import styles from './ProjectScopeDropdown.module.css';

TEXT.DEFINE({
  /** Label for the project scope pill */
  ariaLabel: (projectName: string): string =>
    `Search scope options for ${projectName}`,
  /** Label for the location group in the dropdown */
  scopeLabel: 'Location',
  /** Label for the all-projects scope option */
  allProjectsLabel: 'All Projects',
});

type ProjectScopeDropdownProps = {
  activeProjectId: string;
  scopedToProjectId: string | null;
  onClear: () => void;
  onSetProjectScope?: (projectId: string) => void;
  onFocusInput?: () => void;
  ref?: Ref<HTMLButtonElement>;
};

export const ProjectScopeDropdown = ({
  activeProjectId,
  scopedToProjectId,
  onClear,
  onSetProjectScope,
  onFocusInput,
  ref,
}: ProjectScopeDropdownProps) => {
  const projectData = useScopedToProject(activeProjectId);

  if (!projectData) return null;

  const { name = '', imageUrl } = projectData;
  const isAllProjects = scopedToProjectId === null;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const isDeleteKey = event.key === 'Delete' || event.key === 'Backspace';

    if (isDeleteKey) {
      event.preventDefault();
      onClear();
    }
    if (event.key === 'ArrowRight' && onFocusInput) {
      event.preventDefault();
      onFocusInput();
    }
  };

  const handleScopeChange = (option: string) => {
    if (option === activeProjectId) {
      onSetProjectScope?.(activeProjectId);
    } else {
      onClear();
    }
  };

  return (
    <Menu
      content={
        <MenuRadioGroup
          checked={isAllProjects ? 'account' : activeProjectId}
          label={TEXT.scopeLabel}
          onChange={handleScopeChange}
        >
          <MenuRadio name={activeProjectId}>
            <div className={styles.menuItemContent}>
              <span className={styles.menuItemIcon}>
                <ProjectAvatar
                  id={activeProjectId}
                  size={20}
                  src={imageUrl ?? undefined}
                  title={name}
                />
              </span>
              <Text variant="body">{name}</Text>
            </div>
          </MenuRadio>
          <MenuRadio name="account">
            <div className={styles.menuItemContent}>
              <span className={styles.menuItemIcon}>
                <IconProject color={theme.colors.icon.primary} size={24} />
              </span>
              <Text variant="body">{TEXT.allProjectsLabel}</Text>
            </div>
          </MenuRadio>
        </MenuRadioGroup>
      }
      minWidth="220px"
      placement="bottom-start"
    >
      <button
        ref={ref}
        aria-label={TEXT.ariaLabel(
          isAllProjects ? TEXT.allProjectsLabel : name
        )}
        className={clsx(styles.trigger, {
          [styles.allProjects]: isAllProjects,
        })}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        type="button"
      >
        {!isAllProjects && (
          <div className={styles.chipAvatarContainer}>
            <span className={styles.chipAvatar}>
              <ProjectAvatar
                id={activeProjectId}
                size={20}
                src={imageUrl ?? undefined}
                title={name}
              />
            </span>
          </div>
        )}
        <div className={styles.name}>
          <Text
            color="inherit"
            enableTooltip={true}
            fontWeight="medium"
            truncate={true}
            variant="body"
          >
            {isAllProjects ? TEXT.allProjectsLabel : name}
          </Text>
        </div>
        <IconChevronDown
          className={styles.chevron}
          color="currentColor"
          size={16}
        />
      </button>
    </Menu>
  );
};
